import CachedIcon from '@mui/icons-material/Cached';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { SourcingList, SourcingSavedSearch } from '../../../schemas/SourcingList.schema';
import { SourcingMenuItem } from '../components/SourcingMenu';

interface CommonOptions {
  readonly list: SourcingList | SourcingSavedSearch | null;
  readonly isDuplicating: boolean;
  readonly onRename: () => void;
  readonly onDuplicate: () => void;
  readonly onDelete: () => void;
  readonly onOpenRefreshSettings: () => void;
  readonly onCopyLinkError: (message: string) => void;
}

interface EnrichOrRunOptions {
  readonly isEnrichLoading: boolean;
  readonly isRunLoading: boolean;
  readonly onEnrichNow: () => void;
  readonly onRunNow: () => void;
}

export type BuildListMenuSectionsOptions = CommonOptions &
  (
    | ({ readonly includeEnrichOrRunItem?: true } & EnrichOrRunOptions)
    | { readonly includeEnrichOrRunItem: false }
  );

function buildEnrichOrRunItem(
  options: BuildListMenuSectionsOptions,
  isSavedSearch: boolean,
  isPerson: boolean
): SourcingMenuItem | null {
  if (options.includeEnrichOrRunItem === false) {
    return null;
  }

  const { list } = options;

  if (isSavedSearch) {
    return {
      label: 'Run Now',
      icon: <CachedIcon />,
      onClick: options.onRunNow,
      disabled: !list || options.isRunLoading,
      loading: options.isRunLoading,
    };
  }

  return {
    label: 'Enrich Now',
    icon: <ReadMoreIcon />,
    onClick: options.onEnrichNow,
    disabled: !list || isPerson || options.isEnrichLoading,
    loading: options.isEnrichLoading,
  };
}

function buildDuplicateOrConvertItem(
  isSavedSearch: boolean,
  list: SourcingList | SourcingSavedSearch | null,
  isDuplicating: boolean,
  onDuplicate: () => void
): SourcingMenuItem {
  if (isSavedSearch) {
    return { label: 'Convert to List', icon: <SwapHorizIcon />, onClick: () => {}, disabled: true };
  }

  return {
    label: 'Duplicate',
    icon: <ContentCopyIcon />,
    onClick: onDuplicate,
    disabled: !list,
    loading: isDuplicating,
    keepOpenOnClick: true,
  };
}

export function buildListMenuSections(options: BuildListMenuSectionsOptions): SourcingMenuItem[][] {
  const { list, isDuplicating, onRename, onDuplicate, onDelete, onOpenRefreshSettings, onCopyLinkError } =
    options;
  const isSavedSearch = !list || list.listType === 'active';
  const isPerson = list?.entityType === 'person';

  const onCopyLink = () => {
    if (!list) {
      return;
    }
    const segment = isSavedSearch ? ROUTES.SAVED_SEARCHES : ROUTES.LISTS;
    const url = `${window.location.origin}/${ROUTES.SOURCING}/${segment}/${list.id}`;
    navigator.clipboard.writeText(url).catch(() => {
      onCopyLinkError('Failed to copy link. Please try again.');
    });
  };

  const enrichOrRunItem = buildEnrichOrRunItem(options, isSavedSearch, isPerson);
  const duplicateOrConvertItem = buildDuplicateOrConvertItem(isSavedSearch, list, isDuplicating, onDuplicate);

  return [
    [
      { label: 'Rename', icon: <EditIcon />, onClick: onRename, disabled: isSavedSearch },
      ...(enrichOrRunItem ? [enrichOrRunItem] : []),
      {
        label: isSavedSearch ? 'Refresh Settings' : 'Auto Enrich Settings',
        icon: <TimerOutlinedIcon />,
        onClick: onOpenRefreshSettings,
        disabled: !list,
      },
    ],
    [
      duplicateOrConvertItem,
      { label: 'Copy Link', icon: <LinkIcon />, onClick: onCopyLink, disabled: !list },
    ],
    [{ label: 'Delete', icon: <DeleteOutlineIcon />, onClick: onDelete, disabled: isSavedSearch }],
  ];
}
